import { computed, reactive } from 'vue'
import { calculateShift, groupByPayoutPeriod, summarizeCalculated } from '../lib/calculator'
import { exportDatabase, getAll, getOne, importDatabase, putOne, deleteOne, deleteMany, seedIfEmpty } from '../lib/db'
import { defaultSettings } from '../data/defaults'
import { getWeekPeriod, sortByDateDesc, todayISO } from '../lib/date'
import { makeId } from '../lib/id'
import { resolvePayoutStatus } from '../lib/payout'

function normalizeSettings(settings) {
  const normalized = {
    ...defaultSettings,
    ...(settings || {}),
    quickShift: { ...defaultSettings.quickShift, ...((settings && settings.quickShift) || {}) },
    backup: { ...defaultSettings.backup, ...((settings && settings.backup) || {}) },
    interface: { ...defaultSettings.interface, ...((settings && settings.interface) || {}) }
  }
  if (!['courier', 'picker'].includes(normalized.role)) normalized.role = defaultSettings.role
  if (normalized.role === 'picker') normalized.role = 'courier'
  normalized.weekStartsOn = Number(normalized.weekStartsOn ?? defaultSettings.weekStartsOn)
  normalized.payoutDay = Number(normalized.payoutDay ?? defaultSettings.payoutDay)
  normalized.payoutDelayWeeks = Number(normalized.payoutDelayWeeks ?? defaultSettings.payoutDelayWeeks)
  normalized.weeklyTarget = Math.max(0, Number(normalized.weeklyTarget ?? defaultSettings.weeklyTarget))
  normalized.quickShift.hours = Number(normalized.quickShift.hours || defaultSettings.quickShift.hours)
  normalized.quickShift.orders = Number(normalized.quickShift.orders || defaultSettings.quickShift.orders)
  normalized.interface.compactTables = Boolean(normalized.interface.compactTables)
  normalized.interface.rememberShiftValues = Boolean(normalized.interface.rememberShiftValues)
  return normalized
}

const state = reactive({
  ready: false, loading: false, error: '', settings: normalizeSettings(defaultSettings), conditions: [], rates: [], bonusRules: [], taxRules: [], shifts: [], payouts: [],
  ui: { shiftFormOpen: false, editingShift: null }
})

async function refresh() {
  state.loading = true
  state.error = ''
  try {
    await seedIfEmpty()
    const [settings, conditions, rates, bonusRules, taxRules, shifts, payouts] = await Promise.all([
      getOne('settings', 'main'), getAll('conditions'), getAll('rates'), getAll('bonusRules'), getAll('taxRules'), getAll('shifts'), getAll('payouts')
    ])
    state.settings = normalizeSettings(settings)
    state.conditions = conditions
    state.rates = rates.sort((a, b) => String(b.effectiveFrom).localeCompare(String(a.effectiveFrom)))
    state.bonusRules = bonusRules.sort((a, b) => Number(b.active) - Number(a.active))
    state.taxRules = taxRules.sort((a, b) => Number(b.active) - Number(a.active))
    state.shifts = shifts.sort(sortByDateDesc)
    state.payouts = payouts
    state.ready = true
  } catch (error) {
    console.error(error)
    state.error = error?.message || 'Не удалось загрузить данные из IndexedDB.'
  } finally { state.loading = false }
}

function withId(payload) { return { ...payload, id: payload.id || makeId() } }
async function save(storeName, payload) { await putOne(storeName, withId(payload)); await refresh() }
async function remove(storeName, id) { await deleteOne(storeName, id); await refresh() }
async function removeMany(storeName, ids) { await deleteMany(storeName, ids); await refresh() }
async function saveSettings(payload) {
  const merged = {
    ...state.settings,
    ...payload,
    quickShift: { ...state.settings.quickShift, ...(payload.quickShift || {}) },
    interface: { ...state.settings.interface, ...(payload.interface || {}) },
    backup: { ...state.settings.backup, ...(payload.backup || {}) },
    key: 'main'
  }
  await putOne('settings', normalizeSettings(merged))
  await refresh()
}

function minutesFromTime(value) {
  const [hours = 8, minutes = 0] = String(value || '08:00').split(':').map(Number)
  return (Number.isFinite(hours) ? hours : 8) * 60 + (Number.isFinite(minutes) ? minutes : 0)
}
function timeFromMinutes(value) {
  const total = ((Number(value) % 1440) + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}
async function addQuickShift() {
  const quick = state.settings.quickShift || defaultSettings.quickShift
  const startMinutes = minutesFromTime(quick.startTime)
  const hours = Number(quick.hours || defaultSettings.quickShift.hours)
  const endMinutes = startMinutes + Math.max(1, hours) * 60
  await save('shifts', { date: todayISO(), startTime: timeFromMinutes(startMinutes), endTime: timeFromMinutes(endMinutes), hours: Math.round((Math.max(1, hours)) * 100) / 100, orders: Number(quick.orders || defaultSettings.quickShift.orders), conditionCodes: [], manualAdjustment: 0, comment: 'Быстро добавленная смена' })
}

const calculatedShifts = computed(() => state.shifts.map((shift) => ({ shift, calculation: calculateShift(shift, state) })))
const totalSummary = computed(() => summarizeCalculated(calculatedShifts.value))
const payoutPeriods = computed(() => groupByPayoutPeriod(calculatedShifts.value, state.settings).map((period) => {
  const payout = state.payouts.find((item) => item.periodKey === period.key)
  const actualAmount = Number(payout?.actualAmount || 0)
  const difference = actualAmount - period.summary.net
  const status = resolvePayoutStatus({ expectedAmount: period.summary.net, actualAmount, payoutDate: period.payoutDate, hasPayout: Boolean(payout) })
  return { ...period, payout, actualAmount, difference, status: status.key, statusMeta: status }
}))
const currentWeek = computed(() => {
  const key = getWeekPeriod(todayISO(), state.settings.weekStartsOn).key
  return payoutPeriods.value.find((period) => period.key === key) || null
})

function openShiftForm(shift = null) {
  state.ui.editingShift = shift ? { ...shift, conditionCodes: [...(shift.conditionCodes || [])] } : null
  state.ui.shiftFormOpen = true
}
function closeShiftForm() { state.ui.shiftFormOpen = false; state.ui.editingShift = null }

export function useAppStore() {
  return {
    state, refresh, save, remove, removeMany, saveSettings, addQuickShift, openShiftForm, closeShiftForm,
    calculatedShifts, totalSummary, payoutPeriods, currentWeek, exportDatabase,
    importDatabase: async (payload) => { await importDatabase(payload); await refresh() }
  }
}
